import {
  ActionConfirmationModal,
  AppModal,
  Button,
  CheckboxFormField,
  Form,
  FormSubmitButton,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  retrieveIncidentTypeCategoriesQueryOptions,
  retrieveMatterSettingsQueryOptions,
} from "@/lib/data/react-query";
import {
  useCreateIncidentType,
  useDeleteIncidentType,
  useUpdateIncidentType,
} from "@/lib/data/react-query/matters/matter-type";
import { IncidentTypeSchema } from "@/lib/data/schemas/matters/MatterSchema";

import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type IncidentType } from "@/lib/types";

const INCIDENT_TYPE_FORM_ID = "matter-type-editor-form";

const incidentTypeModalFieldsetClassName =
  "relative grid grid-cols-1 gap-4 sm:grid-cols-2";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditIncidentTypeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      initialIncidentTypeCategoryId?: never;
      activeIncidentType: Pick<IncidentType, "id" | "dateModified"> &
        Partial<IncidentType>;
      setActiveIncidentTypeId: (id: string) => void;
    }
  | {
      isReadOnly?: never;
      editing: false;
      initialIncidentTypeCategoryId?: string;
      activeIncidentType?: never;
      setActiveIncidentTypeId?: never;
    };

const formSchema = IncidentTypeSchema.pick({
  incidentTypeCategoryId: true,
  description: true,
  emailModuleAccess: true,
  expenseModuleAccess: true,
  incidentModuleAccess: true,
  incidentReportModuleAccess: true,
  intakePropertyDamageModuleAccess: true,
  intakeResponsesModuleAccess: true,
  lienModuleAccess: true,
  litigationModuleAccess: true,
  lostWagesModuleAccess: true,
  medicalTreatmentModuleAccess: true,
  settlementModuleAccess: true,
  trustCheckModuleAccess: true,
  injuriesModuleAccess: true,
  insuranceModuleAccess: true,
  signatureRequestModuleAccess: true,
  vehicleModuleAccess: true,
  label: true,
}).extend({ label: z.string().trim().min(1, "Label is required") });

const defaultFormValues = {
  incidentTypeCategoryId: "",
  label: "",
  description: "",
  emailModuleAccess: true,
  expenseModuleAccess: true,
  incidentModuleAccess: true,
  incidentReportModuleAccess: true,
  intakePropertyDamageModuleAccess: true,
  intakeResponsesModuleAccess: true,
  lienModuleAccess: true,
  litigationModuleAccess: true,
  lostWagesModuleAccess: true,
  medicalTreatmentModuleAccess: true,
  settlementModuleAccess: true,
  trustCheckModuleAccess: true,
  injuriesModuleAccess: true,
  insuranceModuleAccess: true,
  signatureRequestModuleAccess: true,
  vehicleModuleAccess: true,
} as const;

export function AddOrEditIncidentTypeModal({
  open,
  setOpen,
  activeIncidentType,
  editing,
  isReadOnly,
  initialIncidentTypeCategoryId,
}: AddOrEditIncidentTypeModalProps & ConditionalProps) {
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const incidentTypes = matterSettingsData?.data.incidentTypes;
  const { data: incidentTypeCategoriesData } = useQuery(
    retrieveIncidentTypeCategoriesQueryOptions(),
  );
  const incidentTypeCategories = incidentTypeCategoriesData?.data ?? [];

  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const editingFormValues = useMemo(() => {
    const editingIncidentType = editing ? activeIncidentType : undefined;
    if (!editingIncidentType) {
      return {
        ...defaultFormValues,
        incidentTypeCategoryId: initialIncidentTypeCategoryId ?? "",
      };
    }

    return {
      ...defaultFormValues,
      ...editingIncidentType,
      emailModuleAccess: editingIncidentType.emailModuleAccess ?? false,
      expenseModuleAccess: editingIncidentType.expenseModuleAccess ?? false,
      incidentModuleAccess: editingIncidentType.incidentModuleAccess ?? false,
      incidentReportModuleAccess:
        editingIncidentType.incidentReportModuleAccess ?? false,
      intakePropertyDamageModuleAccess:
        editingIncidentType.intakePropertyDamageModuleAccess ?? false,
      intakeResponsesModuleAccess:
        editingIncidentType.intakeResponsesModuleAccess ?? false,
      lienModuleAccess: editingIncidentType.lienModuleAccess ?? false,
      litigationModuleAccess:
        editingIncidentType.litigationModuleAccess ?? false,
      lostWagesModuleAccess: editingIncidentType.lostWagesModuleAccess ?? false,
      medicalTreatmentModuleAccess:
        editingIncidentType.medicalTreatmentModuleAccess ?? false,
      settlementModuleAccess:
        editingIncidentType.settlementModuleAccess ?? false,
      trustCheckModuleAccess:
        editingIncidentType.trustCheckModuleAccess ?? false,
      injuriesModuleAccess: editingIncidentType.injuriesModuleAccess ?? false,
      insuranceModuleAccess: editingIncidentType.insuranceModuleAccess ?? false,
      signatureRequestModuleAccess:
        editingIncidentType.signatureRequestModuleAccess ?? false,
      vehicleModuleAccess: editingIncidentType.vehicleModuleAccess ?? false,
      label: editingIncidentType.label ?? "",
    };
  }, [activeIncidentType, editing, initialIncidentTypeCategoryId]);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editingFormValues,
    mode: "onSubmit",
  });

  const createIncidentType = useCreateIncidentType();
  const editIncidentType = useUpdateIncidentType();
  const deleteIncidentType = useDeleteIncidentType();
  const isFormLoading =
    createIncidentType.isPending ||
    editIncidentType.isPending ||
    deleteIncidentType.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    // unique name
    if (
      incidentTypes?.some(
        (incidentType) =>
          toLower(incidentType.label ?? "") === toLower(data.label) &&
          incidentType.id !== activeIncidentType?.id,
      )
    ) {
      form.setError("label", {
        message: "Incident type label must be unique",
      });
      return;
    }

    if (editing) {
      await editIncidentType.mutateAsync({
        id: activeIncidentType.id,
        dateModified: activeIncidentType.dateModified,
        ...Object.fromEntries(
          Object.entries(data).filter(
            ([field]) => form.formState.dirtyFields[field as keyof typeof data],
          ),
        ),
        ...(form.formState.dirtyFields.incidentTypeCategoryId && {
          incidentTypeCategoryId: data.incidentTypeCategoryId || null,
        }),
      });
    } else {
      await createIncidentType.mutateAsync({
        ...data,
        incidentTypeCategoryId: data.incidentTypeCategoryId || null,
      });
    }
    setOpen(false);
    form.reset();
  };

  const handleDelete = async () => {
    setOpen(false);
    if (editing) {
      await deleteIncidentType
        .mutateAsync({
          id: activeIncidentType.id,
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  const sheetTitle = useMemo(
    () =>
      isReadOnly
        ? "View incident type"
        : editing
          ? "Update incident type"
          : "Create incident type",
    [isReadOnly, editing],
  );

  const sheetDescription = useMemo(
    () =>
      isReadOnly
        ? "View the incident type details below"
        : editing
          ? "Update the incident type details below"
          : "Create a new incident type to your firm",
    [isReadOnly, editing],
  );

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const showDeleteInFooter =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.incidentType,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = showDeleteInFooter && (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  );

  const cancelAndSubmitFooter = (
    <div className="flex grow justify-end space-x-2">
      <FormCancelButton
        label={isReadOnly ? "Close" : "Cancel"}
        setOpen={setOpen}
        isDirty={form.formState.isDirty}
      />
      {!isReadOnly && (
        <FormSubmitButton
          submitFormId={INCIDENT_TYPE_FORM_ID}
          loading={isFormLoading}
          disabled={isFormLoading}
        />
      )}
    </div>
  );

  return (
    <>
      {/* Form wraps AppModal so footer controls (FormSubmitButton useFormContext) are inside the provider */}
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={sheetTitle}
          description={sheetDescription}
          size="2xl"
          loading={isFormLoading}
          isDirty={form.formState.isDirty}
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              {deleteButton}
              {cancelAndSubmitFooter}
            </div>
          }
        >
          <form
            id={INCIDENT_TYPE_FORM_ID}
            className="relative"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <fieldset
              disabled={isReadOnly}
              className={incidentTypeModalFieldsetClassName}
            >
              <TextFormField register={form.register("label")} label="Label*" />
              <SimpleSelectFormField
                register={form.register("incidentTypeCategoryId")}
                label="Incident type category"
                items={incidentTypeCategories.map((category) => category.id)}
                formatItemLabel={(id) =>
                  incidentTypeCategories.find((category) => category.id === id)
                    ?.label ?? id
                }
                placeholder="Select incident type category"
              />
              <div className="col-span-2">
                <TextAreaFormField
                  register={form.register("description")}
                  label="Description"
                />
              </div>
              <CheckboxFormField
                register={form.register("emailModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include email module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("expenseModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include expense module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("incidentModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include incident module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("incidentReportModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include incident report module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("intakePropertyDamageModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include intake property damage module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("intakeResponsesModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include intake responses module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("lienModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include lien module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("litigationModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include litigation module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("lostWagesModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include lost wages module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("medicalTreatmentModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include medical treatment module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("settlementModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include settlement module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("trustCheckModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include trust check module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("injuriesModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include injuries module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("insuranceModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include insurance module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("vehicleModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include vehicle module?
                  </div>
                }
              />
              <CheckboxFormField
                register={form.register("signatureRequestModuleAccess")}
                label={
                  <div className="flex items-center gap-x-1">
                    Include signature requests module?
                  </div>
                }
              />
            </fieldset>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title={`Are you sure you want to delete this incident type?`}
        description={`If this incident type is linked to any other items in settings, the link will be removed. \n\n If this incident type is connected to any matters, this deletion will be blocked.`}
        open={isDeleting && !!activeIncidentType}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          if (activeIncidentType) {
            await handleDelete();
          }
        }}
      />
    </>
  );
}
